import { useEffect } from "react";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import * as expressAPI from "@app/services/expressAPI.js";
import { mergeVideos } from "@app/services/ffmpeg.js";
import { jobsPollStatus } from "@app/services/jobs.js";
import { useUpdateVideo } from "@app/hooks/videos.js";
import { fetchPresignURL } from "@app/services/s3.js";

export const expressTemplateKey = {
  all: ["expressTemplates"],
  lists: () => [...expressTemplateKey.all, "list"],
  list: (filters) => [...expressTemplateKey.lists(), { filters }],
  details: () => [...expressTemplateKey.all, "detail"],
  detail: (id) => [...expressTemplateKey.details(), id],
  exports: () => [...expressTemplateKey.all, "export"],
  export: (id, format = "any") => [...expressTemplateKey.exports(), id, format],
  statuses: () => [...expressTemplateKey.all, "status"],
  status: (jobId) => [...expressTemplateKey.statuses(), jobId],
};

export const useExpressTemplates = (options = {}, filters) => {
  const query = useInfiniteQuery({
    queryKey: expressTemplateKey.list(filters),
    queryFn: ({ pageParam, signal }) =>
      expressAPI.getTaggedDocuments(
        { nextUrl: pageParam, filters },
        { signal },
      ),
    initialPageParam: `/beta/tagged-documents?${new URLSearchParams({
      start: 0,
      limit: 25,
    })}`,
    getNextPageParam: (lastPage) => {
      if (lastPage?.paging?.nextUrl) {
        const nextUrl = new URL(lastPage.paging.nextUrl);
        return nextUrl.pathname + nextUrl.search;
      }

      return null;
    },
    ...options,
    staleTime: 30 * 60 * 1000, // 30 minutes
    meta: {
      errorMessage: "Failed to load templates. Please try again.",
    },
  });

  // Automatically fetch the next page when available
  useEffect(() => {
    if (!query.isFetching && query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [query.isFetching, query.hasNextPage, query.fetchNextPage]);

  return query;
};

export const useExpressTemplate = (id, options = {}) => {
  const query = useInfiniteQuery({
    queryKey: expressTemplateKey.detail(id),
    queryFn: ({ pageParam, signal }) =>
      expressAPI.getTaggedDocument(
        { docId: id, nextUrl: pageParam },
        { signal },
      ),
    initialPageParam: `/beta/tagged-documents/${id}?${new URLSearchParams({
      start: 1,
      limit: 10,
    })}`,
    getNextPageParam: (lastPage) => {
      if (lastPage?.paging?.nextUrl) {
        const nextUrl = new URL(lastPage.paging.nextUrl);
        return nextUrl.pathname + nextUrl.search;
      }

      return null;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: !!id,
    meta: {
      errorMessage: "Failed to load template. Please try again.",
    },
    ...options,
  });

  // Automatically fetch the next page when available
  useEffect(() => {
    if (!query.isFetching && query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [query.isFetching, query.hasNextPage, query.fetchNextPage]);

  return query;
};

export const useExpressPollStatus = () => {
  return useMutation({
    // queryKey: expressTemplateKey.status(jobId),
    mutationFn: ({ jobId, interval = 2000 }, { signal }) =>
      expressAPI.pollStatus(jobId, interval, { signal }),
    meta: {
      errorMessage: "Failed to get status. Please try again.",
    },
  });
};

export const useGenerateVariation = () => {
  return useMutation({
    mutationFn: async ({ batchSize, ...payload }, { signal }) => {
      const entries = Object.entries(
        payload.variationDetails?.tagMappings ?? {},
      );

      if (!batchSize || entries.length <= batchSize) {
        const data = await expressAPI.generateVariation(payload, { signal });

        return expressAPI.pollStatus(data.jobId, 2000, { signal });
      }

      // Express times out on large tag sets; apply them in chunks, each on top of the previous result document.
      let id = payload.id;
      let result;
      for (let i = 0; i < entries.length; i += batchSize) {
        const batchPayload = {
          ...payload,
          id,
          variationDetails: {
            ...payload.variationDetails,
            tagMappings: Object.fromEntries(entries.slice(i, i + batchSize)),
          },
        };
        // A freshly generated document fails as input for ~4 minutes; wait that long, then retry every 2 minutes.
        for (let attempt = 0; ; attempt++) {
          if (i > 0 || attempt > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, attempt === 0 ? 240000 : 120000),
            );
            signal?.throwIfAborted();
          }
          try {
            const data = await expressAPI.generateVariation(batchPayload, {
              signal,
            });
            result = await expressAPI.pollStatus(data.jobId, 2000, { signal });
            break;
          } catch (error) {
            if (signal?.aborted || attempt >= 3) throw error;
          }
        }
        id = result.document.id;
      }

      return result;
    },
    // retry: false,
    meta: {
      errorMessage: "Failed to generate variation. Please try again.",
    },
  });
};

export const useExportRendition = () => {
  return useMutation({
    mutationFn: async (payload, { signal }) => {
      const data = await expressAPI.exportRendition(payload, { signal });

      return expressAPI.pollStatus(data.jobId, 2000, { signal });
    },
    // retry: false,
    meta: {
      errorMessage: "Failed to export rendition. Please try again.",
    },
  });
};

export const useMergeVideos = () => {
  const mutation = useMutation({
    mutationFn: async (payload) => {
      // Step 1: Initiate merge
      const { jobId } = await mergeVideos({
        videoUrls: payload.videoUrls,
        timestamps: payload.timestamps,
        s3Path: payload.s3Path,
      });

      // Step 2: Poll for merge status
      const mergeStatus = await jobsPollStatus({ jobId });

      return mergeStatus;
    },
    meta: {
      errorMessage: "Failed to merge videos. Please try again.",
    },
  });

  return mutation;
};

export const useFetchPresignURL = () => {
  const mutation = useMutation({
    mutationFn: async (filePath) => {
      const presignData = await fetchPresignURL(filePath);
      return presignData;
    },
    meta: {
      errorMessage: "Failed to fetch presigned URL. Please try again.",
    },
  });

  return mutation;
};

export const useGenerateVideo = (id) => {
  const updateVideo = useUpdateVideo(id);

  const mutation = useMutation({
    mutationFn: async (payload, { signal }) => {
      const variationDetailsArray = Array.isArray(payload.variationDetails)
        ? payload.variationDetails
        : [payload.variationDetails];

      // Generate variations for each item in parallel
      const variationResults = await Promise.all(
        variationDetailsArray.map((details) =>
          expressAPI.generateVariation(
            { id: payload.id, variationDetails: details },
            { signal },
          ),
        ),
      );

      // Process each variation in parallel
      const videoUrls = await Promise.all(
        variationResults.map(async (variationResult, index) => {
          // Step 1: Poll for generation status
          const generateStatus = await expressAPI.pollStatus(
            variationResult.jobId,
            2000,
            { signal },
          );

          // Step 2: Update video with expressId (only for first variation)
          if (index === 0) {
            await updateVideo.mutateAsync({
              expressId: generateStatus.document.id,
            });
          }

          // Step 3: Export rendition
          const exportResult = await expressAPI.exportRendition(
            {
              id: generateStatus.document.id,
              pages: "1",
              options: {
                format: "video/mp4",
              },
            },
            { signal },
          );

          // Step 4: Poll for export status
          const exportStatus = await expressAPI.pollStatus(
            exportResult.jobId,
            2000,
            { signal },
          );

          // Return video URL
          return exportStatus.pageRenditionsResult?.[0]?.renditionUrl;
        }),
      );

      // Step 5: Merge videos
      const { jobId } = await mergeVideos({
        videoUrls,
        timestamps: [
          { start: 0, duration: 54 },
          { start: 54, duration: 47 },
        ],
        s3Path: payload.filePath,
      });

      // Step 6: Poll for merge status
      const mergeStatus = await jobsPollStatus({ jobId });

      // Step 7: Update video with final path
      await updateVideo.mutateAsync({
        videoPath: mergeStatus?.result?.path,
      });

      // Step 8: Fetch presigned URL for the merged video
      const presignData = await fetchPresignURL(mergeStatus?.result?.path);

      return presignData;
    },
    meta: {
      errorMessage: "Failed to generate video. Please try again.",
    },
  });

  return mutation;
};
